/**
 * AuditLog types — mirror the Mongoose schema.
 */

export type AuditActorType = "user" | "operator" | "system" | "webhook";
export type AuditSeverity = "info" | "warning" | "critical";
export type AuditStatus = "success" | "failure" | "denied";

export interface AuditChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface AuditLogEntry {
  _id: string;
  companyId: string;
  businessId: string | null;

  actorType: AuditActorType;
  actorId: string | null;
  actorEmail: string | null;
  actorName: string | null;
  actorRole: string | null;

  action: string;

  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;

  before: unknown;
  after: unknown;
  changes: AuditChange[];

  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  method: string | null;
  path: string | null;

  status: AuditStatus;
  errorCode: string | null;
  errorMessage: string | null;

  metadata: unknown;
  severity: AuditSeverity;

  createdAt: string;
}

export interface AuditLogSearchFilters {
  from?: string; // ISO date
  to?: string; // ISO date
  action?: string; // exact, or prefix ending in '.' (e.g. 'auth.')
  actorId?: string;
  targetType?: string;
  targetId?: string;
  severity?: AuditSeverity;
  status?: AuditStatus;
  businessId?: string | "null";
  q?: string;
  page?: number;
  limit?: number;
}

export interface AuditLogSearchResponse {
  rows: AuditLogEntry[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ── UI helpers ─────────────────────────────────────────────────

export const SEVERITY_LABELS: Record<AuditSeverity, string> = {
  info: "Info",
  warning: "Warning",
  critical: "Critical",
};

export const STATUS_LABELS: Record<AuditStatus, string> = {
  success: "Success",
  failure: "Failure",
  denied: "Denied",
};

export const ACTOR_TYPE_LABELS: Record<AuditActorType, string> = {
  user: "User",
  operator: "aeTech operator",
  system: "System",
  webhook: "Webhook",
};

/**
 * Human-friendly labels for the most common action names.
 * Falls back to the raw action string if not in the map.
 */
export const ACTION_LABELS: Record<string, string> = {
  "auth.login.success": "User logged in",
  "auth.login.failure": "Login failed",
  "auth.logout": "User logged out",
  "auth.password_changed": "Password changed",
  "auth.password_reset.requested": "Password reset requested",
  "auth.password_reset.completed": "Password reset completed",
  "user.create": "User created",
  "user.update": "User updated",
  "user.delete": "User deleted",
  "user.role_changed": "User role changed",
  "user.business_assigned": "User assigned to business",
  "user.business_unassigned": "User unassigned from business",
  "company.update": "Company settings updated",
  "business.create": "Business created",
  "business.update": "Business updated",
  "business.delete": "Business deleted",
  "subscription.plan_changed": "Subscription plan changed",
  "subscription.renewed": "Subscription renewed",
  "subscription.cancelled": "Subscription cancelled",
  "subscription.payment.success": "Subscription payment succeeded",
  "subscription.payment.failure": "Subscription payment failed",
  "order.complete": "Order completed",
  "order.edit": "Order edited",
  "order.void": "Order voided",
  "expenditure.create": "Expenditure created",
  "expenditure.approve": "Expenditure approved",
  "expenditure.reject": "Expenditure rejected",
  "journal_entry.post": "Journal entry posted",
  "journal_entry.reverse": "Journal entry reversed",
  "inventory_receipt.post": "Inventory receipt posted",
  "stock.adjustment": "Stock adjusted",
  "tax_rate.create": "Tax rate created",
  "tax_rate.update": "Tax rate updated",
  "tax_rate.delete": "Tax rate deleted",
  "custom_field.create": "Custom field created",
  "custom_field.update": "Custom field updated",
  "custom_field.deactivate": "Custom field deactivated",
  "industry.changed": "Industry type changed",
  "operator.impersonate.start": "Operator started impersonation",
  "operator.impersonate.end": "Operator ended impersonation",
  "operator.cross_tenant_view": "Operator viewed tenant data",
  "operator.cross_tenant_action": "Operator acted on tenant data",
  "operator.suspend_tenant": "Operator suspended tenant",
  "operator.reactivate_tenant": "Operator reactivated tenant",
  "access.denied": "Access denied",
};

export function actionLabel(action: string): string {
  return ACTION_LABELS[action] || action;
}
